// Import package components
import { useState, useContext, useCallback, useEffect } from "react";
import { Flex } from "uikit-react";
import { useCookies } from "react-cookie";
import moment from "moment";
import { CSVLink } from "react-csv";

import RecordsPerPage from "../../common/RecordsPerPage";
import Pagination from "../../common/Pagination";
import { AppContexts } from "../../../providers";

import FilterPanel from "../Common/FilterPanel";
import usePath from "../Common/History";

const columns = [
  { label: "ORDER DATE", key: "order_date", format: val => moment.utc(val).local().format("MM/DD/YYYY hh:mm a") },
  { label: "ORDER NUMBER", key: "order_number" },
  { label: "CUSTOMER NAME", key: "name" },
  { label: "CUSTOMER EMAIL", key: "email" },
  { label: "CUSTOMER PHONE", key: "phone" },
  { label: "ITEM NAME", key: "item_name" },
  { label: "ITEM DESCRIPTION", key: "item_description" },
  { label: "QUANTITY", key: "quantity" },
  { label: "LINE TOTAL", key: "line_total" },
  { label: "DATE REQUESTED", key: "date_requested", format: val => val ? moment.utc(val).local().format("MM/DD/YYYY") : "" },
  { label: "CATEGORY", key: "category" },
  { label: "SPECIFICATIONS", key: "specifications" },
];

const OrdersDetails = () => {
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(0);
  const [cookies] = useCookies(["records-per-page"]);
  const { "records-per-page": recordsPerPage = 20 } = cookies;

  const { orderDetails = {}, startDate, endDate, getOrderDetails, active } = useContext(AppContexts.ReportsContext);
  const { total = 0, paginated = [] } = orderDetails;
  const path = usePath();

  useEffect(() => {
    if (path !== active) setPage(0);
  }, [path]);

  useEffect(() => {
    if (active === "orders-details") getFn(startDate, endDate);
  }, [page, filter, recordsPerPage]);

  const getFn = useCallback(
    (startDate, endDate) => {
      if (!startDate || !endDate) {
        return;
      } else {
        getOrderDetails(
          startDate,
          endDate,
          page,
          recordsPerPage
        );
      }
    },
    [page, recordsPerPage]
  );

  const handlePageChange = useCallback((page) => setPage(page), []);
  const handleFilterChange = useCallback((filter) => setFilter(filter), []);

  // Prepare CSV data with formatting
  const csvData = (paginated || []).map(row => {
    const formatted = {};
    columns.forEach(col => {
      if (col.format) {
        formatted[col.label] = col.format(row[col.key]);
      } else {
        formatted[col.label] = row[col.key];
      }
    });
    return formatted;
  });

  return (
    <li className="reports-tab-content">
      <h2>Order Details</h2>
      <div className="uk-card hms-left-tabs-content-card uk-padding-remove">
        <FilterPanel
          onFilterChange={handleFilterChange}
          onSubmit={getFn}
        />
        <CSVLink
          data={csvData}
          headers={columns.map(col => ({ label: col.label, key: col.label }))}
          filename={`order-details-${moment().format("YYYYMMDD-HHmmss")}.csv`}
          className="download"
        >
          <img src="/images/download.svg" uk-tooltip="Download CSV" />
        </CSVLink>
        <div className="secondary">{total} Orders</div>
        <div className="uk-overflow-auto">
          <table className="uk-table uk-table-hover uk-table-divider hms-table uk-margin-small-top">
            <thead>
              <tr>
                {columns.map(col => <th key={col.key}>{col.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {paginated?.length ? (
                paginated.map((item, index) => (
                  <tr key={index}>
                    {columns.map((col, i) => (
                      <td key={i}>{col.format ? col.format(item[col.key]) : item[col.key]}</td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="no-results">
                    No results for this date range
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Flex alignment="between top" className="guest-form-top-filters">
        <Pagination
          count={total}
          index={page}
          onIndexChanged={handlePageChange}
        />
        {total > 0 && <RecordsPerPage />}
      </Flex>
    </li>
  );
};

export default OrdersDetails;
